import React, { useState } from 'react';
import { 
  Book, 
  Server, 
  Workflow, 
  CheckCircle2, 
  XCircle, 
  Code2, 
  Settings, 
  Database,
  Shield,
  Users,
  Briefcase,
  FileText,
  MessageSquare,
  Bell,
  Calendar,
  DollarSign,
  Key,
  Globe,
  Layers,
  GitBranch,
  AlertTriangle,
  Clock,
  ChevronRight,
  Search,
  Copy,
  Check,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

// Feature Status Types
const STATUS = {
  IMPLEMENTED: { label: 'Implemented', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle2 },
  PARTIAL: { label: 'Partial', color: 'bg-amber-500/20 text-amber-400', icon: Clock },
  NOT_IMPLEMENTED: { label: 'Not Implemented', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  BLOCKED: { label: 'Blocked', color: 'bg-purple-500/20 text-purple-400', icon: AlertTriangle },
  PLANNED: { label: 'Planned', color: 'bg-blue-500/20 text-blue-400', icon: GitBranch },
};

// Documentation Data
const SYSTEM_ARCHITECTURE = {
  overview: `LEAP Pro is a comprehensive, multi-tenant SaaS platform for legal practice management. Built with a modern tech stack, it provides law firms with tools for client management, case tracking, document handling, billing, team collaboration, and more.`,
  principles: [
    'Multi-tenant architecture with firm-level data isolation',
    'Role-based access control (RBAC) with granular permissions',
    'RESTful API design with JWT authentication',
    'Real-time capabilities via WebSocket (Socket.IO)',
    'Responsive, mobile-first UI design',
    'Audit logging for compliance and security'
  ],
  stack: {
    frontend: ['React 18', 'Tailwind CSS', 'Shadcn/UI', 'React Router v6', 'Axios', 'Socket.IO Client', 'Recharts'],
    backend: ['FastAPI (Python)', 'Motor (Async MongoDB)', 'Pydantic', 'Python-Jose (JWT)', 'Python-SocketIO'],
    database: ['MongoDB Atlas'],
    infrastructure: ['Kubernetes', 'Docker', 'Nginx Ingress']
  }
};

const CORE_MODULES = [
  {
    name: 'Authentication & Authorization',
    icon: Shield,
    description: 'JWT-based authentication with role-based access control',
    components: ['Login/Register', 'Password Reset', 'Google OAuth', 'Session Management', 'Role Permissions']
  },
  {
    name: 'Client Management',
    icon: Users,
    description: 'Comprehensive client database with contact and matter tracking',
    components: ['Client Profiles', 'Contact Information', 'Client Portal', 'Communication History']
  },
  {
    name: 'Case Management',
    icon: Briefcase,
    description: 'Full lifecycle case tracking from intake to resolution',
    components: ['Case Creation', 'Status Tracking', 'Court Dates', 'Case Documents', 'Charge Codes']
  },
  {
    name: 'Document Management',
    icon: FileText,
    description: 'Document storage, versioning, and template system',
    components: ['File Upload', 'Document Templates', 'Version History', 'Document Generation']
  },
  {
    name: 'Team Chat',
    icon: MessageSquare,
    description: 'Real-time messaging with case-linked conversations',
    components: ['Direct Messages', 'Group Chats', 'Case Chats', 'Reactions', 'Read Receipts']
  },
  {
    name: 'Billing & Invoicing',
    icon: DollarSign,
    description: 'Time tracking, invoicing, and payment processing',
    components: ['Time Entries', 'Invoice Generation', 'Payment Tracking', 'Trust Accounting']
  },
  {
    name: 'Calendar & Tasks',
    icon: Calendar,
    description: 'Scheduling and task management for legal workflows',
    components: ['Event Calendar', 'Task Lists', 'Reminders', 'Court Date Tracking']
  },
  {
    name: 'Platform Admin',
    icon: Settings,
    description: 'Super admin console for platform management',
    components: ['Tenant Management', 'User Management', 'Impersonation', 'System Health', 'Audit Logs']
  }
];

const FEATURES_STATUS = [
  // Authentication
  { module: 'Authentication', feature: 'Email/Password Login', status: 'IMPLEMENTED', notes: 'JWT-based with refresh tokens' },
  { module: 'Authentication', feature: 'Google OAuth Login', status: 'IMPLEMENTED', notes: 'Via Emergent Google Auth' },
  { module: 'Authentication', feature: 'Password Reset', status: 'IMPLEMENTED', notes: 'Email-based reset flow' },
  { module: 'Authentication', feature: 'MFA/2FA', status: 'NOT_IMPLEMENTED', notes: 'Planned for future release' },
  { module: 'Authentication', feature: 'Session Management', status: 'IMPLEMENTED', notes: 'Token expiry and refresh' },
  
  // Clients
  { module: 'Clients', feature: 'Client CRUD', status: 'IMPLEMENTED', notes: 'Full create, read, update, delete' },
  { module: 'Clients', feature: 'Client Search', status: 'IMPLEMENTED', notes: 'Search by name, email, phone' },
  { module: 'Clients', feature: 'Client Portal', status: 'IMPLEMENTED', notes: 'Self-service portal for clients' },
  { module: 'Clients', feature: 'Conflict Check', status: 'IMPLEMENTED', notes: 'Search for conflicts before intake' },
  
  // Cases
  { module: 'Cases', feature: 'Case CRUD', status: 'IMPLEMENTED', notes: 'Full lifecycle management' },
  { module: 'Cases', feature: 'Case Assignment', status: 'IMPLEMENTED', notes: 'Assign team members to cases' },
  { module: 'Cases', feature: 'Charge Codes', status: 'IMPLEMENTED', notes: 'Filtered by practice area' },
  { module: 'Cases', feature: 'Court Dates', status: 'IMPLEMENTED', notes: 'Track hearings and deadlines' },
  { module: 'Cases', feature: 'CanLII Integration', status: 'IMPLEMENTED', notes: 'Canadian legal database lookup' },
  { module: 'Cases', feature: 'Case Templates', status: 'IMPLEMENTED', notes: 'Reusable case configurations' },
  
  // Documents
  { module: 'Documents', feature: 'File Upload', status: 'IMPLEMENTED', notes: 'Multi-file upload support' },
  { module: 'Documents', feature: 'Document Templates', status: 'IMPLEMENTED', notes: 'Create from templates' },
  { module: 'Documents', feature: 'Version History', status: 'PARTIAL', notes: 'Basic versioning implemented' },
  { module: 'Documents', feature: 'E-Signature', status: 'NOT_IMPLEMENTED', notes: 'DocuSign integration planned' },
  { module: 'Documents', feature: 'Document Generation', status: 'IMPLEMENTED', notes: 'PDF generation from templates' },
  
  // Team Chat
  { module: 'Team Chat', feature: 'Direct Messages', status: 'IMPLEMENTED', notes: '1:1 conversations' },
  { module: 'Team Chat', feature: 'Group Chats', status: 'IMPLEMENTED', notes: 'Multi-user conversations' },
  { module: 'Team Chat', feature: 'Case Chats', status: 'IMPLEMENTED', notes: 'Conversations linked to cases' },
  { module: 'Team Chat', feature: 'Message Reactions', status: 'IMPLEMENTED', notes: '8 emoji reactions' },
  { module: 'Team Chat', feature: 'Read Receipts', status: 'IMPLEMENTED', notes: 'Track who read messages' },
  { module: 'Team Chat', feature: 'Typing Indicators', status: 'IMPLEMENTED', notes: 'Real-time typing status' },
  { module: 'Team Chat', feature: 'Global Search', status: 'IMPLEMENTED', notes: 'Search across all chats' },
  { module: 'Team Chat', feature: 'File Attachments', status: 'PARTIAL', notes: 'UI ready, backend pending' },
  
  // Billing
  { module: 'Billing', feature: 'Time Tracking', status: 'IMPLEMENTED', notes: 'Manual time entries' },
  { module: 'Billing', feature: 'Invoice Generation', status: 'IMPLEMENTED', notes: 'PDF invoice creation' },
  { module: 'Billing', feature: 'Payment Processing', status: 'NOT_IMPLEMENTED', notes: 'Stripe integration planned' },
  { module: 'Billing', feature: 'Trust Accounting', status: 'PARTIAL', notes: 'Basic trust ledger' },
  
  // Calendar
  { module: 'Calendar', feature: 'Event Management', status: 'IMPLEMENTED', notes: 'Create and manage events' },
  { module: 'Calendar', feature: 'Task Management', status: 'IMPLEMENTED', notes: 'Task lists with assignments' },
  { module: 'Calendar', feature: 'Reminders', status: 'IMPLEMENTED', notes: 'Email and in-app reminders' },
  { module: 'Calendar', feature: 'Google Calendar Sync', status: 'BLOCKED', notes: 'OAuth redirect URI issue' },
  { module: 'Calendar', feature: 'Outlook Calendar Sync', status: 'NOT_IMPLEMENTED', notes: 'Planned feature' },
  
  // Email
  { module: 'Email', feature: 'Gmail Integration', status: 'BLOCKED', notes: 'Requires OAuth redirect URI update' },
  { module: 'Email', feature: 'Outlook Integration', status: 'NOT_IMPLEMENTED', notes: 'Planned feature' },
  { module: 'Email', feature: 'Email Templates', status: 'IMPLEMENTED', notes: 'Reusable email templates' },
  
  // Notifications
  { module: 'Notifications', feature: 'In-App Notifications', status: 'IMPLEMENTED', notes: 'Bell icon with badge' },
  { module: 'Notifications', feature: 'Push Notifications', status: 'IMPLEMENTED', notes: 'Browser push with settings' },
  { module: 'Notifications', feature: 'Email Notifications', status: 'BLOCKED', notes: 'Depends on email integration' },
  
  // Admin Console
  { module: 'Admin Console', feature: 'Tenant Management', status: 'IMPLEMENTED', notes: 'CRUD + invite tenants' },
  { module: 'Admin Console', feature: 'User Management', status: 'IMPLEMENTED', notes: 'View, edit, disable users' },
  { module: 'Admin Console', feature: 'Impersonation', status: 'IMPLEMENTED', notes: 'Quick access to tenant view' },
  { module: 'Admin Console', feature: 'Metrics Dashboard', status: 'IMPLEMENTED', notes: 'Charts and statistics' },
  { module: 'Admin Console', feature: 'Audit Logs', status: 'IMPLEMENTED', notes: 'Action tracking' },
  { module: 'Admin Console', feature: 'System Health', status: 'IMPLEMENTED', notes: 'Service status monitoring' },
  { module: 'Admin Console', feature: 'OAuth Settings', status: 'IMPLEMENTED', notes: 'Centralized OAuth config' },
  { module: 'Admin Console', feature: 'Feature Flags', status: 'IMPLEMENTED', notes: 'Per-tenant feature toggles' },
  
  // Forms
  { module: 'Forms', feature: 'Form Templates', status: 'IMPLEMENTED', notes: 'Legal form library' },
  { module: 'Forms', feature: 'Form Filling', status: 'IMPLEMENTED', notes: 'Fill out PDF forms' },
  { module: 'Forms', feature: 'Form Auto-Fill', status: 'NOT_IMPLEMENTED', notes: 'Map CRM data to forms' },
  
  // Dashboard
  { module: 'Dashboard', feature: 'Widget System', status: 'IMPLEMENTED', notes: 'Customizable widgets' },
  { module: 'Dashboard', feature: 'Dashboard Presets', status: 'IMPLEMENTED', notes: '5 built-in + custom save' },
  { module: 'Dashboard', feature: 'Drag & Drop', status: 'IMPLEMENTED', notes: 'Reorder widgets' },
];

const API_ENDPOINTS = [
  // Auth
  { method: 'POST', path: '/api/auth/login', description: 'User login', auth: 'None' },
  { method: 'POST', path: '/api/auth/register', description: 'User registration', auth: 'None' },
  { method: 'GET', path: '/api/auth/me', description: 'Get current user', auth: 'JWT' },
  { method: 'POST', path: '/api/auth/refresh', description: 'Refresh token', auth: 'JWT' },
  
  // Clients
  { method: 'GET', path: '/api/clients', description: 'List clients', auth: 'JWT' },
  { method: 'POST', path: '/api/clients', description: 'Create client', auth: 'JWT' },
  { method: 'GET', path: '/api/clients/:id', description: 'Get client', auth: 'JWT' },
  { method: 'PUT', path: '/api/clients/:id', description: 'Update client', auth: 'JWT' },
  { method: 'DELETE', path: '/api/clients/:id', description: 'Delete client', auth: 'JWT' },
  
  // Cases
  { method: 'GET', path: '/api/cases', description: 'List cases', auth: 'JWT' },
  { method: 'POST', path: '/api/cases', description: 'Create case', auth: 'JWT' },
  { method: 'GET', path: '/api/cases/:id', description: 'Get case', auth: 'JWT' },
  { method: 'PUT', path: '/api/cases/:id', description: 'Update case', auth: 'JWT' },
  
  // Chat
  { method: 'GET', path: '/api/chat', description: 'List chats', auth: 'JWT' },
  { method: 'POST', path: '/api/chat/dm', description: 'Create DM', auth: 'JWT' },
  { method: 'POST', path: '/api/chat/group', description: 'Create group', auth: 'JWT' },
  { method: 'POST', path: '/api/chat/case', description: 'Create case chat', auth: 'JWT' },
  { method: 'GET', path: '/api/chat/:id/messages', description: 'Get messages', auth: 'JWT' },
  { method: 'POST', path: '/api/chat/:id/messages', description: 'Send message', auth: 'JWT' },
  { method: 'POST', path: '/api/chat/:id/messages/:mid/reactions', description: 'Add reaction', auth: 'JWT' },
  
  // Admin
  { method: 'POST', path: '/api/admin/auth/login', description: 'Admin login', auth: 'None' },
  { method: 'GET', path: '/api/admin/tenants', description: 'List tenants', auth: 'Platform JWT' },
  { method: 'GET', path: '/api/admin/users', description: 'List users', auth: 'Platform JWT' },
  { method: 'PUT', path: '/api/admin/users/:id', description: 'Update user', auth: 'Platform JWT' },
  { method: 'POST', path: '/api/admin/impersonate/start', description: 'Start impersonation', auth: 'Platform JWT' },
];

const DATA_MODELS = [
  {
    name: 'users',
    description: 'User accounts within tenants',
    fields: ['user_id', 'email', 'name', 'role', 'firm_id', 'is_active', 'created_at', 'last_login']
  },
  {
    name: 'firms',
    description: 'Tenant/law firm organizations',
    fields: ['firm_id', 'name', 'plan', 'subscription_status', 'trial_ends_at', 'settings']
  },
  {
    name: 'clients',
    description: 'Client records',
    fields: ['client_id', 'firm_id', 'first_name', 'last_name', 'email', 'phone', 'type', 'status']
  },
  {
    name: 'cases',
    description: 'Legal cases/matters',
    fields: ['case_id', 'firm_id', 'client_id', 'title', 'practice_area', 'status', 'assigned_users']
  },
  {
    name: 'documents',
    description: 'Document storage',
    fields: ['document_id', 'case_id', 'firm_id', 'filename', 'file_type', 'file_url', 'version']
  },
  {
    name: 'chats',
    description: 'Chat conversations',
    fields: ['chat_id', 'tenant_id', 'type', 'name', 'case_id', 'last_message_at']
  },
  {
    name: 'messages',
    description: 'Chat messages',
    fields: ['message_id', 'chat_id', 'user_id', 'content', 'attachments', 'created_at']
  },
  {
    name: 'platform_users',
    description: 'Platform admin accounts',
    fields: ['user_id', 'email', 'name', 'role', 'permissions', 'is_active']
  },
  {
    name: 'audit_logs',
    description: 'System audit trail',
    fields: ['log_id', 'actor_user_id', 'action_type', 'tenant_id', 'details', 'timestamp']
  }
];

const ENV_VARIABLES = [
  { name: 'MONGO_URL', location: 'backend/.env', description: 'MongoDB connection string', required: true },
  { name: 'DB_NAME', location: 'backend/.env', description: 'Database name', required: true },
  { name: 'JWT_SECRET', location: 'backend/.env', description: 'JWT signing secret', required: true },
  { name: 'GOOGLE_CLIENT_ID', location: 'backend/.env', description: 'Google OAuth client ID', required: false },
  { name: 'GOOGLE_CLIENT_SECRET', location: 'backend/.env', description: 'Google OAuth secret', required: false },
  { name: 'EMERGENT_LLM_KEY', location: 'backend/.env', description: 'Emergent universal API key', required: false },
  { name: 'REACT_APP_BACKEND_URL', location: 'frontend/.env', description: 'Backend API URL', required: true },
];

const KNOWN_ISSUES = [
  {
    id: 'ISSUE-001',
    severity: 'HIGH',
    title: 'Gmail Integration Blocked',
    description: 'OAuth redirect URI changes with each preview environment, blocking Gmail integration.',
    workaround: 'User must manually update redirect URI in Google Cloud Console for each environment.',
    affected: ['Email sending', 'Team invites', 'Client invites']
  },
  {
    id: 'ISSUE-002',
    severity: 'MEDIUM',
    title: 'WebSocket Connection in Preview',
    description: 'WebSocket may not connect in certain preview environments due to proxy configuration.',
    workaround: 'Real-time features fall back to polling. Full functionality in production.',
    affected: ['Team Chat real-time updates', 'Typing indicators']
  },
  {
    id: 'ISSUE-003',
    severity: 'LOW',
    title: 'File Upload Size Limit',
    description: 'Large file uploads (>50MB) may timeout due to proxy limits.',
    workaround: 'Use chunked uploads for large files.',
    affected: ['Document uploads']
  }
];

// Copy to clipboard helper
const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
  toast.success('Copied to clipboard');
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = STATUS[status] || STATUS.NOT_IMPLEMENTED;
  const Icon = config.icon;
  return (
    <Badge className={`${config.color} gap-1`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// Main Documentation Component
export default function SystemDocumentation() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('overview');
  const [statusFilter, setStatusFilter] = useState('all');

  const sections = [
    { id: 'overview', label: 'System Overview', icon: Layers },
    { id: 'architecture', label: 'Architecture', icon: Server },
    { id: 'features', label: 'Features & Workflows', icon: Workflow },
    { id: 'status', label: 'Feature Status', icon: CheckCircle2 },
    { id: 'technical', label: 'Technical Details', icon: Code2 },
    { id: 'operations', label: 'Operations', icon: Settings },
  ];

  // Filter features by search and status
  const filteredFeatures = FEATURES_STATUS.filter(f => {
    const matchesSearch = !searchQuery || 
      f.feature.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.module.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.notes.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || f.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Group features by module
  const groupedFeatures = filteredFeatures.reduce((acc, f) => {
    if (!acc[f.module]) acc[f.module] = [];
    acc[f.module].push(f);
    return acc;
  }, {});

  // Stats
  const stats = {
    total: FEATURES_STATUS.length,
    implemented: FEATURES_STATUS.filter(f => f.status === 'IMPLEMENTED').length,
    partial: FEATURES_STATUS.filter(f => f.status === 'PARTIAL').length,
    notImplemented: FEATURES_STATUS.filter(f => f.status === 'NOT_IMPLEMENTED').length,
    blocked: FEATURES_STATUS.filter(f => f.status === 'BLOCKED').length,
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="system-documentation">
      {/* Header */}
      <div className="border-b border-slate-700 bg-slate-800/50 sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center">
                <Book className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">System Documentation</h1>
                <p className="text-sm text-slate-400">LEAP Pro Technical Reference</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64 bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <Badge className="bg-emerald-500/20 text-emerald-400">
                v3.1 - January 2026
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* Sidebar Navigation */}
          <div className="w-64 flex-shrink-0">
            <Card className="bg-slate-800/50 border-slate-700 sticky top-24">
              <CardContent className="p-2">
                <nav className="space-y-1">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          activeSection === section.id
                            ? 'bg-blue-600 text-white'
                            : 'text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {section.label}
                      </button>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* System Overview */}
            {activeSection === 'overview' && (
              <div className="space-y-6">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Layers className="h-5 w-5 text-blue-400" />
                      System Overview
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      High-level understanding of LEAP Pro
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h3 className="text-white font-medium mb-2">What is LEAP Pro?</h3>
                      <p className="text-slate-300 leading-relaxed">
                        {SYSTEM_ARCHITECTURE.overview}
                      </p>
                    </div>

                    <Separator className="bg-slate-700" />

                    <div>
                      <h3 className="text-white font-medium mb-3">Design Principles</h3>
                      <ul className="space-y-2">
                        {SYSTEM_ARCHITECTURE.principles.map((principle, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-300">
                            <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-1 flex-shrink-0" />
                            {principle}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Separator className="bg-slate-700" />

                    <div>
                      <h3 className="text-white font-medium mb-3">Core Modules</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {CORE_MODULES.map((module) => {
                          const Icon = module.icon;
                          return (
                            <div key={module.name} className="p-4 bg-slate-700/50 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Icon className="h-5 w-5 text-blue-400" />
                                <h4 className="text-white font-medium">{module.name}</h4>
                              </div>
                              <p className="text-slate-400 text-sm mb-2">{module.description}</p>
                              <div className="flex flex-wrap gap-1">
                                {module.components.slice(0, 3).map((comp) => (
                                  <Badge key={comp} variant="outline" className="text-slate-300 border-slate-600 text-xs">
                                    {comp}
                                  </Badge>
                                ))}
                                {module.components.length > 3 && (
                                  <Badge variant="outline" className="text-slate-400 border-slate-600 text-xs">
                                    +{module.components.length - 3} more
                                  </Badge>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-4">
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-emerald-400">{stats.implemented}</p>
                      <p className="text-sm text-slate-400">Implemented</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-amber-400">{stats.partial}</p>
                      <p className="text-sm text-slate-400">Partial</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-red-400">{stats.notImplemented}</p>
                      <p className="text-sm text-slate-400">Not Implemented</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-purple-400">{stats.blocked}</p>
                      <p className="text-sm text-slate-400">Blocked</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Architecture */}
            {activeSection === 'architecture' && (
              <div className="space-y-6">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Server className="h-5 w-5 text-blue-400" />
                      System Architecture
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Architecture Diagram Description */}
                    <div className="p-6 bg-slate-700/50 rounded-lg">
                      <h3 className="text-white font-medium mb-4">High-Level Architecture</h3>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="p-4 bg-blue-600/20 rounded-lg border border-blue-500/30">
                          <Globe className="h-8 w-8 text-blue-400 mx-auto mb-2" />
                          <p className="text-white font-medium">Frontend</p>
                          <p className="text-slate-400 text-sm">React SPA</p>
                          <p className="text-slate-500 text-xs">Port 3000</p>
                        </div>
                        <div className="p-4 bg-emerald-600/20 rounded-lg border border-emerald-500/30">
                          <Server className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                          <p className="text-white font-medium">Backend</p>
                          <p className="text-slate-400 text-sm">FastAPI</p>
                          <p className="text-slate-500 text-xs">Port 8001</p>
                        </div>
                        <div className="p-4 bg-amber-600/20 rounded-lg border border-amber-500/30">
                          <Database className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                          <p className="text-white font-medium">Database</p>
                          <p className="text-slate-400 text-sm">MongoDB</p>
                          <p className="text-slate-500 text-xs">Atlas Cloud</p>
                        </div>
                      </div>
                      <div className="mt-4 text-center">
                        <p className="text-slate-400 text-sm">
                          All API calls are prefixed with <code className="bg-slate-600 px-1 rounded">/api</code> and routed through Kubernetes Ingress
                        </p>
                      </div>
                    </div>

                    <Separator className="bg-slate-700" />

                    {/* Tech Stack */}
                    <div>
                      <h3 className="text-white font-medium mb-4">Technology Stack</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-slate-700/50 rounded-lg">
                          <h4 className="text-blue-400 font-medium mb-2">Frontend</h4>
                          <div className="flex flex-wrap gap-2">
                            {SYSTEM_ARCHITECTURE.stack.frontend.map((tech) => (
                              <Badge key={tech} className="bg-blue-500/20 text-blue-300">{tech}</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="p-4 bg-slate-700/50 rounded-lg">
                          <h4 className="text-emerald-400 font-medium mb-2">Backend</h4>
                          <div className="flex flex-wrap gap-2">
                            {SYSTEM_ARCHITECTURE.stack.backend.map((tech) => (
                              <Badge key={tech} className="bg-emerald-500/20 text-emerald-300">{tech}</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="p-4 bg-slate-700/50 rounded-lg">
                          <h4 className="text-amber-400 font-medium mb-2">Database</h4>
                          <div className="flex flex-wrap gap-2">
                            {SYSTEM_ARCHITECTURE.stack.database.map((tech) => (
                              <Badge key={tech} className="bg-amber-500/20 text-amber-300">{tech}</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="p-4 bg-slate-700/50 rounded-lg">
                          <h4 className="text-purple-400 font-medium mb-2">Infrastructure</h4>
                          <div className="flex flex-wrap gap-2">
                            {SYSTEM_ARCHITECTURE.stack.infrastructure.map((tech) => (
                              <Badge key={tech} className="bg-purple-500/20 text-purple-300">{tech}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Features & Workflows */}
            {activeSection === 'features' && (
              <div className="space-y-6">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Workflow className="h-5 w-5 text-blue-400" />
                      Features & Workflows
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Detailed breakdown of system functionality
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Accordion type="multiple" className="space-y-2">
                      {CORE_MODULES.map((module) => {
                        const Icon = module.icon;
                        return (
                          <AccordionItem key={module.name} value={module.name} className="border border-slate-700 rounded-lg px-4">
                            <AccordionTrigger className="text-white hover:no-underline">
                              <div className="flex items-center gap-2">
                                <Icon className="h-5 w-5 text-blue-400" />
                                {module.name}
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-300">
                              <p className="mb-4">{module.description}</p>
                              <div className="space-y-2">
                                <h4 className="text-white text-sm font-medium">Components:</h4>
                                <ul className="grid grid-cols-2 gap-2">
                                  {module.components.map((comp) => (
                                    <li key={comp} className="flex items-center gap-2 text-sm">
                                      <ChevronRight className="h-3 w-3 text-slate-500" />
                                      {comp}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </CardContent>
                </Card>

                {/* User Roles */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Role-Based Access Control</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">Role</TableHead>
                          <TableHead className="text-slate-400">Scope</TableHead>
                          <TableHead className="text-slate-400">Key Permissions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow className="border-slate-700">
                          <TableCell className="text-white font-medium">Platform Owner</TableCell>
                          <TableCell className="text-slate-300">Global</TableCell>
                          <TableCell className="text-slate-300">All platform operations, tenant management, impersonation</TableCell>
                        </TableRow>
                        <TableRow className="border-slate-700">
                          <TableCell className="text-white font-medium">Support Agent</TableCell>
                          <TableCell className="text-slate-300">Global (Read)</TableCell>
                          <TableCell className="text-slate-300">View tenants, read-only impersonation</TableCell>
                        </TableRow>
                        <TableRow className="border-slate-700">
                          <TableCell className="text-white font-medium">Firm Admin</TableCell>
                          <TableCell className="text-slate-300">Tenant</TableCell>
                          <TableCell className="text-slate-300">Full firm management, user management, settings</TableCell>
                        </TableRow>
                        <TableRow className="border-slate-700">
                          <TableCell className="text-white font-medium">Senior Lawyer</TableCell>
                          <TableCell className="text-slate-300">Tenant</TableCell>
                          <TableCell className="text-slate-300">Case management, client access, billing</TableCell>
                        </TableRow>
                        <TableRow className="border-slate-700">
                          <TableCell className="text-white font-medium">Associate</TableCell>
                          <TableCell className="text-slate-300">Assigned Cases</TableCell>
                          <TableCell className="text-slate-300">Work on assigned cases, limited billing</TableCell>
                        </TableRow>
                        <TableRow className="border-slate-700">
                          <TableCell className="text-white font-medium">Paralegal</TableCell>
                          <TableCell className="text-slate-300">Assigned Cases</TableCell>
                          <TableCell className="text-slate-300">Document preparation, research, no billing</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Feature Status */}
            {activeSection === 'status' && (
              <div className="space-y-6">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-white flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-blue-400" />
                          Feature Status Matrix
                        </CardTitle>
                        <CardDescription className="text-slate-400">
                          Implementation status of all features
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        {Object.entries(STATUS).map(([key, config]) => (
                          <Button
                            key={key}
                            variant={statusFilter === key ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
                            className={statusFilter === key ? '' : 'border-slate-600 text-slate-300'}
                          >
                            {config.label} ({FEATURES_STATUS.filter(f => f.status === key).length})
                          </Button>
                        ))}
                        <Button
                          variant={statusFilter === 'all' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setStatusFilter('all')}
                          className={statusFilter === 'all' ? '' : 'border-slate-600 text-slate-300'}
                        >
                          All ({stats.total})
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[600px]">
                      {Object.entries(groupedFeatures).map(([module, features]) => (
                        <div key={module} className="mb-6">
                          <h3 className="text-white font-medium mb-3 sticky top-0 bg-slate-800 py-2">{module}</h3>
                          <Table>
                            <TableHeader>
                              <TableRow className="border-slate-700">
                                <TableHead className="text-slate-400">Feature</TableHead>
                                <TableHead className="text-slate-400">Status</TableHead>
                                <TableHead className="text-slate-400">Notes</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {features.map((feature) => (
                                <TableRow key={`${module}-${feature.feature}`} className="border-slate-700">
                                  <TableCell className="text-white">{feature.feature}</TableCell>
                                  <TableCell><StatusBadge status={feature.status} /></TableCell>
                                  <TableCell className="text-slate-400 text-sm">{feature.notes}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ))}
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Known Issues */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-400" />
                      Known Issues & Blockers
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {KNOWN_ISSUES.map((issue) => (
                      <div key={issue.id} className="p-4 bg-slate-700/50 rounded-lg border-l-4 border-amber-500">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={issue.severity === 'HIGH' ? 'bg-red-500/20 text-red-400' : issue.severity === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}>
                            {issue.severity}
                          </Badge>
                          <span className="text-slate-400 text-sm">{issue.id}</span>
                        </div>
                        <h4 className="text-white font-medium mb-1">{issue.title}</h4>
                        <p className="text-slate-300 text-sm mb-2">{issue.description}</p>
                        <p className="text-slate-400 text-sm"><strong>Workaround:</strong> {issue.workaround}</p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {issue.affected.map((area) => (
                            <Badge key={area} variant="outline" className="text-slate-400 border-slate-600 text-xs">
                              {area}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Technical Details */}
            {activeSection === 'technical' && (
              <div className="space-y-6">
                {/* API Endpoints */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Code2 className="h-5 w-5 text-blue-400" />
                      API Endpoints
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Key REST API endpoints
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-slate-700">
                            <TableHead className="text-slate-400 w-20">Method</TableHead>
                            <TableHead className="text-slate-400">Endpoint</TableHead>
                            <TableHead className="text-slate-400">Description</TableHead>
                            <TableHead className="text-slate-400 w-24">Auth</TableHead>
                            <TableHead className="text-slate-400 w-12"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {API_ENDPOINTS.map((endpoint, i) => (
                            <TableRow key={i} className="border-slate-700">
                              <TableCell>
                                <Badge className={
                                  endpoint.method === 'GET' ? 'bg-emerald-500/20 text-emerald-400' :
                                  endpoint.method === 'POST' ? 'bg-blue-500/20 text-blue-400' :
                                  endpoint.method === 'PUT' ? 'bg-amber-500/20 text-amber-400' :
                                  'bg-red-500/20 text-red-400'
                                }>
                                  {endpoint.method}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-white font-mono text-sm">{endpoint.path}</TableCell>
                              <TableCell className="text-slate-300">{endpoint.description}</TableCell>
                              <TableCell className="text-slate-400 text-sm">{endpoint.auth}</TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyToClipboard(endpoint.path)}
                                  className="text-slate-400 hover:text-white"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Data Models */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Database className="h-5 w-5 text-blue-400" />
                      Data Models
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      MongoDB collections and key fields
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {DATA_MODELS.map((model) => (
                        <div key={model.name} className="p-4 bg-slate-700/50 rounded-lg">
                          <h4 className="text-white font-medium font-mono mb-1">{model.name}</h4>
                          <p className="text-slate-400 text-sm mb-2">{model.description}</p>
                          <div className="flex flex-wrap gap-1">
                            {model.fields.map((field) => (
                              <Badge key={field} variant="outline" className="text-slate-300 border-slate-600 text-xs font-mono">
                                {field}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Operations */}
            {activeSection === 'operations' && (
              <div className="space-y-6">
                {/* Environment Variables */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Key className="h-5 w-5 text-blue-400" />
                      Environment Variables
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Required configuration
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">Variable</TableHead>
                          <TableHead className="text-slate-400">Location</TableHead>
                          <TableHead className="text-slate-400">Description</TableHead>
                          <TableHead className="text-slate-400 w-24">Required</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ENV_VARIABLES.map((env) => (
                          <TableRow key={env.name} className="border-slate-700">
                            <TableCell className="text-white font-mono">{env.name}</TableCell>
                            <TableCell className="text-slate-400 text-sm">{env.location}</TableCell>
                            <TableCell className="text-slate-300">{env.description}</TableCell>
                            <TableCell>
                              {env.required ? (
                                <Badge className="bg-red-500/20 text-red-400">Required</Badge>
                              ) : (
                                <Badge className="bg-slate-500/20 text-slate-400">Optional</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* File Structure */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-400" />
                      Key File Locations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="font-mono text-sm space-y-1 p-4 bg-slate-900 rounded-lg">
                      <p className="text-slate-400">/app/</p>
                      <p className="text-slate-300 pl-4">├── backend/</p>
                      <p className="text-slate-400 pl-8">├── server.py          <span className="text-slate-500"># Main FastAPI app</span></p>
                      <p className="text-slate-400 pl-8">├── routers/           <span className="text-slate-500"># API route modules</span></p>
                      <p className="text-slate-400 pl-8">│   ├── admin.py       <span className="text-slate-500"># Admin console APIs</span></p>
                      <p className="text-slate-400 pl-8">│   ├── chat.py        <span className="text-slate-500"># Team chat APIs</span></p>
                      <p className="text-slate-400 pl-8">│   └── forms.py       <span className="text-slate-500"># Legal forms APIs</span></p>
                      <p className="text-slate-400 pl-8">├── websocket_manager.py <span className="text-slate-500"># Real-time events</span></p>
                      <p className="text-slate-400 pl-8">├── requirements.txt</p>
                      <p className="text-slate-400 pl-8">└── .env</p>
                      <p className="text-slate-300 pl-4">├── frontend/</p>
                      <p className="text-slate-400 pl-8">├── src/</p>
                      <p className="text-slate-400 pl-12">├── pages/           <span className="text-slate-500"># Page components</span></p>
                      <p className="text-slate-400 pl-12">├── components/      <span className="text-slate-500"># Reusable UI</span></p>
                      <p className="text-slate-400 pl-12">├── contexts/        <span className="text-slate-500"># React contexts</span></p>
                      <p className="text-slate-400 pl-12">├── hooks/           <span className="text-slate-500"># Custom hooks</span></p>
                      <p className="text-slate-400 pl-12">└── lib/             <span className="text-slate-500"># Utilities, API</span></p>
                      <p className="text-slate-400 pl-8">├── package.json</p>
                      <p className="text-slate-400 pl-8">└── .env</p>
                      <p className="text-slate-300 pl-4">└── memory/</p>
                      <p className="text-slate-400 pl-8">└── PRD.md             <span className="text-slate-500"># Product requirements</span></p>
                    </div>
                  </CardContent>
                </Card>

                {/* Deployment */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Server className="h-5 w-5 text-blue-400" />
                      Deployment & Operations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <h4 className="text-white font-medium mb-2">Service Management</h4>
                      <div className="font-mono text-sm space-y-1">
                        <p className="text-slate-300"><span className="text-emerald-400">$</span> sudo supervisorctl status</p>
                        <p className="text-slate-300"><span className="text-emerald-400">$</span> sudo supervisorctl restart backend</p>
                        <p className="text-slate-300"><span className="text-emerald-400">$</span> sudo supervisorctl restart frontend</p>
                      </div>
                    </div>
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <h4 className="text-white font-medium mb-2">Log Locations</h4>
                      <div className="font-mono text-sm space-y-1">
                        <p className="text-slate-300">/var/log/supervisor/backend.err.log</p>
                        <p className="text-slate-300">/var/log/supervisor/frontend.err.log</p>
                      </div>
                    </div>
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <h4 className="text-white font-medium mb-2">Hot Reload</h4>
                      <p className="text-slate-300 text-sm">
                        Both frontend and backend have hot reload enabled. Code changes are automatically detected.
                        Restart is only needed for .env changes or new dependency installations.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
